import { Link } from "react-router-dom";
import { ArrowRight, Truck } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Counter from "@/components/Counter";
import { Icon } from "@/lib/icon";
import {
    FLEET, WAREHOUSES, TECH_STACK, PERFORMANCE_COUNTERS,
    WAREHOUSE_IMAGE, FLEET_IMAGE, WAREHOUSE_ALT_IMAGE
} from "@/lib/site-data";

export default function Infrastructure() {
    return (
        <div data-testid="page-infrastructure">
            <PageHeader
                overline="Infrastructure & Fleet"
                title="100+ vehicles. Owned warehouses. Operational discipline."
                subtitle="A purpose-built logistics infrastructure designed around the manufacturing belt of western India."
                image={FLEET_IMAGE}
                breadcrumb={[{ label: "Infrastructure" }]}
                testId="infra-header"
            />

            {/* Fleet */}
            <section className="bg-white py-20 md:py-24" data-testid="infra-fleet">
                <div className="container-vl">
                    <div className="grid grid-cols-12 gap-8 items-end mb-12">
                        <div className="col-span-12 md:col-span-7">
                            <p className="overline mb-4">Fleet</p>
                            <h2 className="heading-h2 text-[#101A2C]">100+ operational vehicles<br />on the road, every day.</h2>
                        </div>
                        <div className="col-span-12 md:col-span-5">
                            <p className="body-lg">A balanced mix of LCV, MCV, FTL and trailer assets — owned and dedicated — covering city milk-runs to ODC project cargo.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                        {FLEET.map((f, i) => (
                            <div key={f.type} className="bg-[#101A2C] text-white p-7 border-b-4 border-[#F4A000]" data-testid={`fleet-${i}`}>
                                <Truck className="h-9 w-9 text-[#F4A000] mb-5" strokeWidth={1.5} />
                                <p className="font-display text-4xl mb-1 text-[#F4A000]">{f.count}</p>
                                <p className="font-display uppercase text-lg mb-3">{f.type}</p>
                                <p className="text-xs text-[#CBD5E1] leading-relaxed">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Warehouses */}
            <section className="bg-[#F8FAFC] py-20 md:py-24 border-y border-[#CBD5E1]" data-testid="infra-warehouses">
                <div className="container-vl grid grid-cols-12 gap-10 items-start">
                    <div className="col-span-12 lg:col-span-5">
                        <img src={WAREHOUSE_ALT_IMAGE} alt="Warehouse interior" className="w-full h-72 lg:h-[420px] object-cover border border-[#CBD5E1]" loading="lazy" />
                    </div>
                    <div className="col-span-12 lg:col-span-7">
                        <p className="overline mb-4">Warehousing</p>
                        <h2 className="heading-h2 text-[#101A2C] mb-6">Owned facilities at Waluj & Chakan — plus built-to-suit capability up to 120,000 sqft.</h2>
                        <div className="space-y-4">
                            {WAREHOUSES.map((w, i) => (
                                <div key={w.location} className="bg-white border border-[#CBD5E1] p-6 flex flex-col md:flex-row md:items-center gap-4 hover:border-[#28406A] transition-colors" data-testid={`warehouse-${i}`}>
                                    <div className="md:w-1/3">
                                        <p className="overline mb-1">{w.sqft}</p>
                                        <h3 className="font-display uppercase text-lg text-[#101A2C]">{w.location}</h3>
                                    </div>
                                    <p className="text-sm text-[#4B6390] md:flex-1">{w.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Tech stack */}
            <section className="bg-[#101A2C] text-white py-20 md:py-24 grain-overlay" data-testid="infra-tech">
                <div className="container-vl">
                    <p className="overline mb-4">Technology Stack</p>
                    <h2 className="heading-h2 text-white mb-14 max-w-3xl">A control room for every consignment.</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#28406A]">
                        {TECH_STACK.map((t, i) => (
                            <div key={t.title} className="bg-[#101A2C] p-7 hover:bg-[#28406A] transition-colors" data-testid={`infra-tech-${i}`}>
                                <Icon name={t.icon} className="h-8 w-8 text-[#F4A000] mb-4" strokeWidth={1.5} />
                                <h3 className="font-display uppercase text-lg mb-2">{t.title}</h3>
                                <p className="text-sm text-[#CBD5E1] leading-relaxed">{t.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Ops dashboard infographic */}
            <section className="bg-[#F4A000] py-20" data-testid="infra-ops">
                <div className="container-vl">
                    <p className="overline mb-3 text-[#101A2C]">Operations Dashboard</p>
                    <h2 className="heading-h2 text-[#101A2C] mb-14 max-w-3xl">Track record, in numbers.</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#101A2C]/15">
                        {PERFORMANCE_COUNTERS.map((c) => (
                            <div key={c.label} className="bg-[#F4A000] p-4 sm:p-7 min-w-0 overflow-hidden" data-testid={`infra-counter-${c.testId}`}>
                                <p className="font-display font-bold text-[#101A2C] whitespace-nowrap" style={{ fontSize: "clamp(1.25rem, 4vw, 3.5rem)", lineHeight: 1 }}>
                                    <Counter to={c.value} suffix={c.suffix} />
                                </p>
                                <p className="mt-3 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#101A2C]/80 font-semibold">{c.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white py-16" data-testid="infra-cta">
                <div className="container-vl flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <h3 className="heading-h3 text-[#101A2C] max-w-2xl">Want to visit our warehouses? Schedule an on-site audit.</h3>
                    <Link to="/contact" data-testid="infra-cta-btn" className="btn-navy">
                        Book a Site Visit <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
